import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Banknote,
  Plus,
  Check,
  X,
  Clock,
  Loader2,
  FileText,
  Building2,
  User,
  Calendar,
  Filter,
  Receipt,
} from "lucide-react";

const CATEGORY_LABELS = {
  office_supplies: "Office Supplies",
  transport: "Transport",
  refreshments: "Refreshments",
  cleaning: "Cleaning",
  minor_repairs: "Minor Repairs",
  utilities: "Utilities",
  miscellaneous: "Miscellaneous",
};

const PettyCashManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [maxAmount, setMaxAmount] = useState(5000);
  const [statusFilter, setStatusFilter] = useState("");
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [newRequest, setNewRequest] = useState({
    amount: "",
    category: "",
    description: "",
    receipt_url: "",
  });
  const [reviewNotes, setReviewNotes] = useState("");

  const isDataEntry = user?.role === "data_entry";
  const canApprove = user?.role === "accounts" || user?.role === "admin";

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const [requestsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/petty-cash/requests`, {
          params: { status: statusFilter || undefined },
          withCredentials: true,
        }),
        axios.get(`${API}/petty-cash/categories`, { withCredentials: true }),
      ]);

      setRequests(requestsRes.data.requests || []);
      setCategories(categoriesRes.data.categories || []);
      setMaxAmount(categoriesRes.data.max_amount || 5000);

      if (canApprove) {
        const statsRes = await axios.get(`${API}/petty-cash/stats`, { withCredentials: true });
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch petty cash data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!newRequest.amount || !newRequest.category || !newRequest.description) {
      toast.error("Please fill in amount, category, and description");
      return;
    }

    const amount = parseFloat(newRequest.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (amount > maxAmount) {
      toast.error(`Amount cannot exceed K${maxAmount.toLocaleString()}`);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/petty-cash/requests`, {
        ...newRequest,
        amount: amount,
      }, { withCredentials: true });

      toast.success("Petty cash request submitted!");
      setShowNewRequest(false);
      setNewRequest({ amount: "", category: "", description: "", receipt_url: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReview = async (status) => {
    if (!selectedRequest) return;

    setSubmitting(true);
    try {
      await axios.put(`${API}/petty-cash/requests/${selectedRequest.request_id}/review`, {
        status,
        review_notes: reviewNotes,
      }, { withCredentials: true });

      toast.success(`Request ${status}`);
      setShowReview(false);
      setSelectedRequest(null);
      setReviewNotes("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to review request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (requestId) => {
    if (!window.confirm("Cancel this request?")) return;

    try {
      await axios.delete(`${API}/petty-cash/requests/${requestId}`, { withCredentials: true });
      toast.success("Request cancelled");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel request");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-700",
      approved: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700",
    };
    const icons = {
      pending: <Clock className="w-3 h-3" />,
      approved: <Check className="w-3 h-3" />,
      rejected: <X className="w-3 h-3" />,
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${styles[status]}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="petty-cash-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Petty Cash Requests</h1>
          <p className="text-gray-500">
            {isDataEntry
              ? "Submit petty cash requests for approval"
              : "Review and approve petty cash requests"}
          </p>
        </div>
        {(isDataEntry || canApprove) && (
          <Button onClick={() => setShowNewRequest(true)} data-testid="new-request-btn">
            <Plus className="w-4 h-4 mr-2" /> New Request
          </Button>
        )}
      </div>

      {/* Stats (for Accounts/Admin) */}
      {stats && canApprove && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <p className="text-sm text-gray-500">Total Requests</p>
            <p className="text-2xl font-bold">{stats.total_requests}</p>
          </div>
          <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
            <p className="text-sm text-yellow-600">Pending</p>
            <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
          </div>
          <div className="bg-green-50 rounded-lg border border-green-200 p-4">
            <p className="text-sm text-green-600">Approved</p>
            <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
          </div>
          <div className="bg-red-50 rounded-lg border border-red-200 p-4">
            <p className="text-sm text-red-600">Rejected</p>
            <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
          </div>
          <div className="bg-primary/5 rounded-lg border border-primary/20 p-4">
            <p className="text-sm text-gray-500">This Month</p>
            <p className="text-2xl font-bold text-primary">K{stats.month_total_approved?.toLocaleString() || 0}</p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            className="border rounded-md px-3 py-2 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <span className="text-sm text-gray-500">{requests.length} requests</span>
      </div>

      {/* Requests List */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Request</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Amount</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Category</th>
              {canApprove && (
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Branch</th>
              )}
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {requests.map((req) => (
              <tr key={req.request_id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div>
                    <p className="font-medium text-sm">{req.description}</p>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {req.requested_by_name}
                    </p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="font-semibold text-primary">K{req.amount?.toLocaleString()}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm">{CATEGORY_LABELS[req.category] || req.category}</span>
                </td>
                {canApprove && (
                  <td className="px-4 py-3">
                    <span className="text-sm flex items-center gap-1">
                      <Building2 className="w-3 h-3 text-gray-400" />
                      {req.branch?.name || req.branch?.code || "N/A"}
                    </span>
                  </td>
                )}
                <td className="px-4 py-3">{getStatusBadge(req.status)}</td>
                <td className="px-4 py-3">
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(req.created_at)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {req.status === "pending" && canApprove && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedRequest(req);
                          setShowReview(true);
                        }}
                      >
                        Review
                      </Button>
                    )}
                    {req.status === "pending" && req.requested_by === user?.user_id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCancel(req.request_id)}
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                    {req.receipt_url && (
                      <Button size="sm" variant="ghost" asChild>
                        <a href={req.receipt_url} target="_blank" rel="noopener noreferrer">
                          <Receipt className="w-4 h-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {requests.length === 0 && (
              <tr>
                <td colSpan={canApprove ? 7 : 6} className="px-4 py-12 text-center text-gray-500">
                  <Banknote className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No petty cash requests found</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* New Request Dialog */}
      <Dialog open={showNewRequest} onOpenChange={setShowNewRequest}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Petty Cash Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Amount (K) *</label>
              <Input
                type="number"
                min="1"
                max={maxAmount}
                value={newRequest.amount}
                onChange={(e) => setNewRequest({ ...newRequest, amount: e.target.value })}
                placeholder={`Max K${maxAmount.toLocaleString()}`}
              />
              <p className="text-xs text-gray-500 mt-1">Maximum: K{maxAmount.toLocaleString()}</p>
            </div>
            <div>
              <label className="text-sm font-medium">Category *</label>
              <select
                className="w-full border rounded-md p-2"
                value={newRequest.category}
                onChange={(e) => setNewRequest({ ...newRequest, category: e.target.value })}
              >
                <option value="">Select category...</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {CATEGORY_LABELS[cat] || cat}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Description *</label>
              <Textarea
                value={newRequest.description}
                onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                placeholder="What is this expense for?"
                rows={3}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Receipt URL (optional)</label>
              <Input
                value={newRequest.receipt_url}
                onChange={(e) => setNewRequest({ ...newRequest, receipt_url: e.target.value })}
                placeholder="Link to uploaded receipt"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowNewRequest(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmitRequest} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={showReview} onOpenChange={setShowReview}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Review Petty Cash Request</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Amount</span>
                  <span className="font-semibold text-primary">K{selectedRequest.amount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Category</span>
                  <span>{CATEGORY_LABELS[selectedRequest.category] || selectedRequest.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Requested By</span>
                  <span>{selectedRequest.requested_by_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Branch</span>
                  <span>{selectedRequest.branch?.name || "N/A"}</span>
                </div>
                <div className="pt-2 border-t">
                  <span className="text-sm text-gray-500">Description</span>
                  <p className="mt-1">{selectedRequest.description}</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Review Notes (optional)</label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes about your decision..."
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => handleReview("rejected")}
                  disabled={submitting}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button
                  onClick={() => handleReview("approved")}
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PettyCashManagement;
